import { FilesApi } from '@up2stay/up2promo-api-client';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Dropzone,
  DropzoneContent,
  DropzoneEmptyState,
  Label,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import {
  ImageCrop,
  ImageCropApply,
  ImageCropContent,
  ImageCropReset,
  ImageCropZoom,
} from '@up2stay/up2promo-ui/components/shadcn-io/image-crop';
import { MoreHorizontal, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { Image } from '@/core/components/ui/image';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { base64ToFile } from '@/core/utils/base64-to-file';

/** Matches backend multer allow-list for image uploads (no SVG/HEIC). */
const ACCEPTED_IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/pjpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

const ACCEPTED_IMAGE_EXTENSIONS = new Set([
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
]);

const DROPZONE_ACCEPT: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
};

/** Avoid `image/*` — that lets the picker offer SVG/HEIC. */
const FILE_INPUT_ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/** Wide banner crops (profile header ~3:1) — keep retina sharpness without huge PNGs. */
const HEADER_MAX_OUTPUT_EDGE = 2400;
const HEADER_JPEG_QUALITY = 0.92;

type ImageAcceptResult = 'ok' | 'heic' | 'unsupported';

interface LogoUploadProps {
  label: string;
  description: string;
  currentPreview?: string;
  onUpload: (fileId: string, preview: string) => void;
  onRemove: () => void;
  /** Crop aspect ratio; defaults to square (1). */
  aspect?: number;
  /**
   * Compact controls for strips that already show the avatar —
   * hide the large dropzone preview, keep change/remove actions.
   */
  compact?: boolean;
  /** Hide the label/description (e.g. when strip owns the heading). */
  hideCaption?: boolean;
}

function resolvePreviewSrc(preview?: string): string | undefined {
  if (!preview) return undefined;
  if (
    preview.startsWith('data:') ||
    preview.startsWith('blob:') ||
    preview.startsWith('http://') ||
    preview.startsWith('https://')
  ) {
    return preview;
  }
  return getAbsoluteFileUrl(preview);
}

function fileExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  if (dot < 0) return '';
  return fileName.slice(dot).toLowerCase();
}

function classifyImageFile(file: File): ImageAcceptResult {
  const mime = (file.type || '').toLowerCase().trim();
  const ext = fileExtension(file.name);

  if (
    mime.includes('heic') ||
    mime.includes('heif') ||
    ext === '.heic' ||
    ext === '.heif'
  ) {
    return 'heic';
  }

  if (mime && ACCEPTED_IMAGE_MIME_TYPES.has(mime)) {
    return 'ok';
  }

  // iOS/Android often leave type empty or use odd aliases — trust extension.
  if (ACCEPTED_IMAGE_EXTENSIONS.has(ext)) {
    return 'ok';
  }

  return 'unsupported';
}

export function LogoUpload({
  label,
  description,
  currentPreview,
  onUpload,
  onRemove,
  aspect = 1,
  compact = false,
  hideCaption = false,
}: LogoUploadProps) {
  const dict = useDictionary();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const previewSrc = resolvePreviewSrc(currentPreview);

  const unsupportedFormatMessage =
    dict.settings.avatarUpload.unsupportedFormat ??
    'Use JPG, PNG, GIF, or WebP. SVG and other formats are not supported.';
  const heicFormatMessage =
    dict.settings.avatarUpload.heicUnsupported ??
    'HEIC photos are not supported. Please choose a JPG or PNG.';

  const rejectFile = (result: Exclude<ImageAcceptResult, 'ok'>) => {
    toast.error(
      result === 'heic' ? heicFormatMessage : unsupportedFormatMessage,
    );
  };

  const openCropForFile = (file: File) => {
    const result = classifyImageFile(file);
    if (result !== 'ok') {
      rejectFile(result);
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(dict.settings.avatarUpload.acceptsImages);
      return;
    }

    // Keep the dialog mounted; set file then open so Vaul/Dialog animate reliably.
    setSelectedFile(file);
    setCropDialogOpen(true);
  };

  const handleFileSelect = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    openCropForFile(file);
  };

  const handlePickerError = () => {
    toast.error(unsupportedFormatMessage);
  };

  const closeCropDialog = () => {
    setCropDialogOpen(false);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropComplete = async (croppedImageDataUrl: string) => {
    if (!selectedFile) return;

    setIsUploading(true);
    setCropDialogOpen(false);

    const isWideBanner = aspect !== 1;
    const mimeType = isWideBanner ? 'image/jpeg' : 'image/png';
    const extension = isWideBanner ? '.jpg' : '.png';

    try {
      const croppedFile = base64ToFile(
        croppedImageDataUrl,
        mimeType,
        selectedFile.name.replace(/\.[^.]+$/, '') + extension,
      );

      const uploadResponse = await FilesApi.uploadFile({
        body: {
          file: croppedFile,
        },
      });

      if (uploadResponse.data) {
        // Prefer the API file path for storage/preview. Never force callers to
        // persist the crop data-URL as a path (that breaks getAbsoluteFileUrl).
        const uploaded = uploadResponse.data.file;
        const previewPath =
          typeof uploaded.path === 'string' && uploaded.path.trim() !== ''
            ? uploaded.path
            : croppedImageDataUrl;
        onUpload(uploaded.id, previewPath);
        toast.success(dict.settings.avatarUpload.uploaded);
      }
    } catch (err) {
      console.error('Failed to upload logo:', err);
      toast.error(dict.settings.avatarUpload.uploadFailed);
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const hiddenFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept={FILE_INPUT_ACCEPT}
      className="sr-only"
      tabIndex={-1}
      aria-hidden
      onChange={(event) => {
        const files = event.target.files ? Array.from(event.target.files) : [];
        handleFileSelect(files);
        // Allow selecting the same file again later.
        event.target.value = '';
      }}
    />
  );

  const cropDialog = (
    <ResponsiveDialog
      open={cropDialogOpen}
      handleOnly
      onOpenChange={(open) => {
        if (!open) {
          closeCropDialog();
        } else {
          setCropDialogOpen(true);
        }
      }}
    >
      <ResponsiveDialogContent className="sm:max-w-[500px]" scrollable={false}>
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>
            {dict.settings.avatarUpload.change}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription className="whitespace-pre-line">
            {dict.settings.avatarUpload.cropDialogDescription}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>
        {selectedFile ? (
          <ImageCrop
            file={selectedFile}
            aspect={aspect}
            onCrop={handleCropComplete}
            {...(aspect !== 1
              ? {
                  outputMimeType: 'image/jpeg',
                  outputQuality: HEADER_JPEG_QUALITY,
                  maxOutputEdge: HEADER_MAX_OUTPUT_EDGE,
                }
              : {})}
          >
            <div className="flex flex-col gap-4">
              <div
                data-vaul-no-drag
                className="flex touch-none justify-center overscroll-contain rounded-lg border bg-muted p-4"
              >
                <ImageCropContent className="max-h-[min(400px,50dvh)] max-w-full" />
              </div>
              <ImageCropZoom label={dict.settings.avatarUpload.zoom} />
              <ResponsiveDialogFooter className="gap-2 sm:gap-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeCropDialog}
                >
                  {dict.settings.avatarUpload.cancel}
                </Button>
                <ImageCropReset asChild>
                  <Button type="button" variant="outline">
                    {dict.settings.avatarUpload.reset}
                  </Button>
                </ImageCropReset>
                <ImageCropApply asChild>
                  <Button type="button">
                    {dict.settings.avatarUpload.cropAndUpload}
                  </Button>
                </ImageCropApply>
              </ResponsiveDialogFooter>
            </div>
          </ImageCrop>
        ) : null}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );

  if (compact) {
    const primaryLabel = previewSrc
      ? dict.settings.avatarUpload.change
      : dict.settings.avatarUpload.upload;

    return (
      <div className="space-y-2">
        {!hideCaption ? (
          <>
            <Label>{label}</Label>
            <p className="text-sm text-muted-foreground">{description}</p>
          </>
        ) : null}
        <div className="flex flex-wrap items-center gap-1.5">
          {hiddenFileInput}
          <Button
            type="button"
            size="sm"
            disabled={isUploading || cropDialogOpen}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="size-4" />
            {primaryLabel}
          </Button>
          {previewSrc ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  disabled={isUploading}
                  aria-label={dict.settings.avatarUpload.remove}
                >
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem
                  onClick={onRemove}
                  className="text-destructive focus:text-destructive"
                >
                  <X className="size-4" />
                  {dict.settings.avatarUpload.remove}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
        {cropDialog}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {!hideCaption ? (
        <>
          <Label>{label}</Label>
          <p className="text-sm text-muted-foreground">{description}</p>
        </>
      ) : null}
      <div className="space-y-4">
        {hiddenFileInput}
        <Dropzone
          accept={DROPZONE_ACCEPT}
          maxFiles={1}
          maxSize={MAX_IMAGE_BYTES}
          onDrop={handleFileSelect}
          onError={handlePickerError}
          disabled={isUploading || cropDialogOpen}
          src={previewSrc ? [] : undefined}
          className="border-dashed"
          translations={{
            clickToReplace: dict.settings.avatarUpload.change,
            dragAndDrop: dict.settings.avatarUpload.dragAndDrop,
          }}
        >
          <DropzoneEmptyState>
            <div className="flex min-w-0 max-w-full flex-col items-center justify-center gap-2 py-2">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-muted">
                <Upload className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="min-w-0 max-w-full text-center">
                <p className="text-pretty break-words font-medium text-sm">
                  {isUploading
                    ? dict.settings.avatarUpload.uploading
                    : dict.settings.avatarUpload.dragAndDrop}
                </p>
                <p className="text-pretty break-words text-muted-foreground text-xs">
                  {dict.settings.avatarUpload.acceptsImages}
                </p>
              </div>
            </div>
          </DropzoneEmptyState>
          <DropzoneContent>
            {previewSrc ? (
              <div className="flex flex-col items-center justify-center gap-3 py-4">
                <div
                  className={
                    aspect === 1
                      ? 'h-32 w-32 overflow-hidden rounded-lg border-2 border-border'
                      : 'h-28 w-full max-w-sm overflow-hidden rounded-lg border-2 border-border'
                  }
                >
                  <Image
                    key={previewSrc}
                    src={previewSrc}
                    alt={label}
                    className="h-full w-full rounded-lg object-cover"
                  />
                </div>
                <p className="text-muted-foreground text-xs">
                  {dict.settings.avatarUpload.change}
                </p>
              </div>
            ) : null}
          </DropzoneContent>
        </Dropzone>

        {previewSrc ? (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRemove}
              disabled={isUploading}
            >
              <X className="mr-2 h-4 w-4" />
              {dict.settings.avatarUpload.remove}
            </Button>
          </div>
        ) : null}
      </div>
      {cropDialog}
    </div>
  );
}
